/**
 * STUFE 3: Browser-seitiger Cache Service für intelligente lokale Speicherung
 * Implementiert Multi-Layer Caching mit localStorage und Memory Cache
 */

class BrowserCacheService {
  constructor() {
    this.memoryCache = new Map();
    this.STORAGE_PREFIX = 'eachotter_cache_';
    
    // Cache-TTLs in Millisekunden
    this.TTL = {
      PROFILES: 5 * 60 * 1000,      // 5 Minuten für Profile
      SEARCH: 2 * 60 * 1000,        // 2 Minuten für Search-Ergebnisse  
      SKILLS: 30 * 60 * 1000,       // 30 Minuten für Skills
      SESSIONS: 1 * 60 * 1000,      // 1 Minute für Sessions
      USER_PROFILE: 10 * 60 * 1000, // 10 Minuten für User-Profil
      NOTIFICATIONS: 2 * 60 * 1000  // 2 Minuten für Benachrichtigungs-Liste
    };

    // Maximale Cache-Größe
    this.MAX_MEMORY_ENTRIES = 100;
    this.MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB

    // Auto-Cleanup alle 5 Minuten
    this.startAutoCleanup();
  }

  /**
   * Speichert Daten im Cache (Memory + localStorage)
   */
  set(key, data, ttl = this.TTL.PROFILES) {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        ttl,
        expires: Date.now() + ttl
      };

      // 1. Memory Cache (schnellster Zugriff)
      this.setMemoryCache(key, cacheEntry);

      // 2. localStorage (persistiert über Browser-Sessions)
      this.setLocalStorage(key, cacheEntry);

      console.log('💾 Cache Set:', { 
        key: key.substring(0, 30) + '...', 
        ttl: ttl / 1000 + 's',
        size: JSON.stringify(data).length 
      });

      return true;
    } catch (error) {
      console.warn('❌ Cache Set Error:', error.message);
      return false;
    }
  }

  /**
   * Ruft Daten aus dem Cache ab (Memory -> localStorage)
   */
  get(key) {
    try {
      // 1. Versuche Memory Cache (schnellster Zugriff)
      let cacheEntry = this.memoryCache.get(key);
      
      if (cacheEntry) {
        if (this.isExpired(cacheEntry)) {
          this.memoryCache.delete(key);
          cacheEntry = null;
        } else {
          console.log('⚡ Memory Cache Hit:', key.substring(0, 30) + '...');
          return cacheEntry.data;
        }
      }

      // 2. Versuche localStorage (langsamer, aber persistent)
      cacheEntry = this.getLocalStorage(key);
      
      if (cacheEntry) {
        if (this.isExpired(cacheEntry)) {
          this.removeLocalStorage(key);
          return null;
        } else {
          // Lade zurück in Memory Cache für zukünftige Zugriffe
          this.setMemoryCache(key, cacheEntry);
          console.log('📁 LocalStorage Cache Hit:', key.substring(0, 30) + '...');
          return cacheEntry.data;
        }
      }

      console.log('❌ Cache Miss:', key.substring(0, 30) + '...');
      return null;
    } catch (error) {
      console.warn('❌ Cache Get Error:', error.message);
      return null;
    }
  }

  /**
   * Memory Cache Management
   */
  setMemoryCache(key, cacheEntry) {
    // Limit Memory Cache Size
    if (this.memoryCache.size >= this.MAX_MEMORY_ENTRIES) {
      const firstKey = this.memoryCache.keys().next().value;
      this.memoryCache.delete(firstKey);
    }
    
    this.memoryCache.set(key, cacheEntry);
  }

  /**
   * localStorage Management
   */
  setLocalStorage(key, cacheEntry) {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const serialized = JSON.stringify(cacheEntry);
      
      // Check storage size limit
      if (this.getStorageSize() + serialized.length > this.MAX_STORAGE_SIZE) {
        this.cleanupOldestEntries();
      }
      
      localStorage.setItem(storageKey, serialized);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('📦 LocalStorage Quota exceeded, cleaning up...');
        this.cleanupOldestEntries();
        // Retry after cleanup
        try {
          localStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(cacheEntry));
        } catch (retryError) {
          console.error('❌ Cache storage failed after cleanup:', retryError.message);
        }
      }
    }
  }

  getLocalStorage(key) {
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const serialized = localStorage.getItem(storageKey);
      return serialized ? JSON.parse(serialized) : null;
    } catch (error) {
      console.warn('❌ LocalStorage Parse Error:', error.message);
      // Clean up corrupted entry
      localStorage.removeItem(this.STORAGE_PREFIX + key);
      return null;
    }
  }

  removeLocalStorage(key) {
    localStorage.removeItem(this.STORAGE_PREFIX + key);
  }

  /**
   * Cache-spezifische Methoden
   */
  
  // Profile Cache
  setProfiles(userId, page, profiles, hasMore = false, totalCount = undefined) {
    const key = `profiles_${userId}_${page}`;
    const payload = { profiles, hasMore, userId, page };
    if (totalCount !== undefined) {
      payload.totalCount = totalCount;
    }
    return this.set(key, payload, this.TTL.PROFILES);
  }

  getProfiles(userId, page) {
    const key = `profiles_${userId}_${page}`;
    return this.get(key);
  }

  // Clear all cache for a specific user (helpful for demo users)
  clearUserCache(userId) {
    try {
      // Clear memory cache
      for (const key of this.memoryCache.keys()) {
        if (key.includes(userId)) {
          this.memoryCache.delete(key);
        }
      }
      
      // Clear localStorage cache
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.includes(userId)) {
          localStorage.removeItem(key);
        }
      }
      
      console.log('🗑️ Cache cleared for user:', userId);
      return true;
    } catch (error) {
      console.error('❌ Error clearing user cache:', error);
      return false;
    }
  }

  // Search Cache
  // meta (optional): { hasMore, totalCount } der ersten Seite, damit "Weitere laden"
  // auch nach einem Cache-Treffer erscheint.
  setSearchResults(searchTerm, results, meta = {}) {
    const key = `search_${this.hashString(searchTerm)}`;
    return this.set(key, { searchTerm, results, ...meta }, this.TTL.SEARCH);
  }

  getSearchResults(searchTerm) {
    const key = `search_${this.hashString(searchTerm)}`;
    return this.get(key);
  }

  // Skills Cache
  setSkills(schoolId, skills) {
    const key = `skills_${schoolId}`;
    return this.set(key, skills, this.TTL.SKILLS);
  }

  getSkills(schoolId) {
    const key = `skills_${schoolId}`;
    return this.get(key);
  }

  // User Profile Cache
  setUserProfile(userId, profile) {
    const key = `user_profile_${userId}`;
    return this.set(key, profile, this.TTL.USER_PROFILE);
  }

  getUserProfile(userId) {
    const key = `user_profile_${userId}`;
    return this.get(key);
  }

  /**
   * Utility Methods
   */
  
  isExpired(cacheEntry) {
    return Date.now() > cacheEntry.expires;
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  getStorageSize() {
    let totalSize = 0;
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        totalSize += localStorage[key].length;
      }
    }
    return totalSize;
  }

  cleanupOldestEntries() {
    console.log('🧹 Cleaning up old cache entries...');
    
    const entries = [];
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const cacheEntry = JSON.parse(localStorage[key]);
          entries.push({ key, timestamp: cacheEntry.timestamp });
        } catch (error) {
          // Remove corrupted entries
          localStorage.removeItem(key);
        }
      }
    }

    // Sort by timestamp and remove oldest 25%
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const removeCount = Math.ceil(entries.length * 0.25);
    
    for (let i = 0; i < removeCount; i++) {
      localStorage.removeItem(entries[i].key);
    }

    console.log(`🗑️ Removed ${removeCount} old cache entries`);
  }

  cleanupExpired() {
    let removedCount = 0;

    // Cleanup Memory Cache
    for (let [key, cacheEntry] of this.memoryCache) {
      if (this.isExpired(cacheEntry)) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }

    // Cleanup localStorage
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const cacheEntry = JSON.parse(localStorage[key]);
          if (this.isExpired(cacheEntry)) {
            localStorage.removeItem(key);
            removedCount++;
          }
        } catch (error) {
          // Remove corrupted entries
          localStorage.removeItem(key);
          removedCount++;
        }
      }
    }

    if (removedCount > 0) {
      console.log(`🧹 Cleaned up ${removedCount} expired cache entries`);
    }

    return removedCount;
  }

  startAutoCleanup() {
    if (this.cleanupIntervalId) return; // Bereits gestartet

    // Cleanup expired entries every 5 minutes (nicht im Hintergrund - Akku)
    this.cleanupIntervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      this.cleanupExpired();
    }, 5 * 60 * 1000);

    // Initial cleanup
    this.initialCleanupTimeoutId = setTimeout(() => {
      this.cleanupExpired();
    }, 10000); // After 10 seconds
  }

  stopAutoCleanup() {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }
    if (this.initialCleanupTimeoutId) {
      clearTimeout(this.initialCleanupTimeoutId);
      this.initialCleanupTimeoutId = null;
    }
  }

  /**
   * Invalidation Methods
   */
  
  invalidateUser(userId) {
    console.log('🗑️ Invalidating cache for user:', userId);
    
    // Remove from memory cache
    for (let key of this.memoryCache.keys()) {
      if (key.includes(userId)) {
        this.memoryCache.delete(key);
      }
    }

    // Remove from localStorage
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX) && key.includes(userId)) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Invalidiert alle Session-Listen-Caches (Keys mit Präfix "sessions_")
   * Aufrufen nach Session-Mutationen: Create, Join, Leave, Start, Complete, Cancel, Update
   */
  invalidateSessions() {
    console.log('🗑️ Invalidating all session caches');

    for (let key of this.memoryCache.keys()) {
      if (key.startsWith('sessions_')) {
        this.memoryCache.delete(key);
      }
    }

    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX + 'sessions_')) {
        localStorage.removeItem(key);
      }
    }
  }

  invalidateSearch() {
    console.log('🗑️ Invalidating all search cache');

    // Remove search entries from memory cache
    for (let key of this.memoryCache.keys()) {
      if (key.startsWith('search_')) {
        this.memoryCache.delete(key);
      }
    }

    // Remove search entries from localStorage
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX + 'search_')) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Invalidiert alle Connection-bezogenen Caches
   * Aufrufen nach: Accept, Decline, Unmatch
   * 🆕 Hinzugefügt: 22. Jan 2026
   */
  invalidateConnections() {
    console.log('🗑️ Invalidating all connection caches');

    // Remove connection status entries from memory cache
    for (let key of this.memoryCache.keys()) {
      if (key.startsWith('connection_') || key.startsWith('chat_') || key.startsWith('conversations_')) {
        this.memoryCache.delete(key);
      }
    }

    // Remove from localStorage
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX + 'connection_') ||
          key.startsWith(this.STORAGE_PREFIX + 'chat_') ||
          key.startsWith(this.STORAGE_PREFIX + 'conversations_')) {
        localStorage.removeItem(key);
      }
    }

    // Auch Search-Cache invalidieren (wegen ProfileCard Button-Status)
    this.invalidateSearch();
  }

  clear() {
    console.log('🗑️ Clearing all cache');
    
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage entries
    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Cache Statistics
   */
  getStats() {
    const memoryEntries = this.memoryCache.size;
    let localStorageEntries = 0;
    let totalSize = 0;

    for (let key in localStorage) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        localStorageEntries++;
        totalSize += localStorage[key].length;
      }
    }

    return {
      memoryEntries,
      localStorageEntries,
      totalStorageSize: totalSize,
      maxStorageSize: this.MAX_STORAGE_SIZE,
      storageUsagePercent: Math.round((totalSize / this.MAX_STORAGE_SIZE) * 100)
    };
  }
}

// Singleton-Instanz
const browserCacheService = new BrowserCacheService();

export default browserCacheService;
